/**
 * Workspace Default Model Picker
 *
 * Flat row design matching the Copilot chat sidebar model picker: each row
 * shows [star] [name] [provider] [multiplier] [tier pill]. Groups are broken
 * only by capability (Chat, Image, Embedding, …) via a discreet spacing gap,
 * not by explicit headings. Tier metadata is shared with the chat picker
 * via `blocksuite/ai/utils/model-tier.ts`.
 */

import { notify } from '@affine/component';
import {
  sortModelsByTierAndCost,
  tierLabel,
  tierPillStyle,
} from '@affine/core/blocksuite/ai/utils/model-tier';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface AIModel {
  id: string;
  name: string;
  providerId: string;
  providerName: string;
  capabilities: string[];
  tier: string;
  status: string;
  displayMultiplier: number;
  isDefaultForCapability: string | null;
  enabled: boolean;
  workspaceDisabled?: boolean;
  configProviderId?: string;
  configModelId?: string;
}

interface CapabilityGroup {
  capability: string;
  models: AIModel[];
}

const CAPABILITY_ORDER = [
  'chat',
  'image',
  'embedding',
  'rowMatching',
  'webSearch',
  'stt',
] as const;

const SETTINGS_CAPABILITIES = new Set<string>(['chat']);

/** Capabilities hidden from the workspace settings UI */
const HIDDEN_CAPABILITIES = new Set(['vision', 'rowMatching']);

// =============================================================================
// Hook
// =============================================================================

/** Workspace default model IDs per capability */
interface WorkspaceDefaultsMap {
  [capability: string]: string | null;
}

interface RegistryModelsResponse {
  readonly models?: AIModel[];
}

interface RegistryProvidersResponse {
  readonly providers?: Array<{
    readonly id: string;
    readonly name: string;
  }>;
}

interface WorkspaceDefaultsResponse {
  readonly defaults?: Record<string, unknown>;
}

interface ProviderConfigResponse {
  readonly providers?: Array<{
    readonly provider?: { readonly id?: string };
    readonly enabled?: boolean;
    readonly allowedTiers?: string[];
    readonly disabledModelIds?: string[];
  }>;
}

function workspaceSettingsRequest(
  workspaceId: string,
  path: string,
  init?: RequestInit,
  mode: 'background' | 'foreground' = 'background'
): Promise<Response> {
  return defaultWorkspaceApiClient.request({
    workspaceId,
    path,
    init,
    category: 'settings',
    key: `${init?.method ?? 'GET'}:${path}`,
    mode,
    priority: mode === 'foreground' ? 'foreground' : 'visible',
  });
}

function workspaceSettingsJson<T>(
  workspaceId: string,
  path: string,
  init?: RequestInit,
  mode: 'background' | 'foreground' = 'background'
): Promise<T> {
  return defaultWorkspaceApiClient.requestJson<T>({
    workspaceId,
    path,
    init,
    category: 'settings',
    key: `${init?.method ?? 'GET'}:${path}`,
    mode,
    priority: mode === 'foreground' ? 'foreground' : 'visible',
  });
}

function useCapabilityModels(workspaceId: string) {
  const [capabilityGroups, setCapabilityGroups] = useState<CapabilityGroup[]>(
    []
  );
  const [workspaceDefaults, setWorkspaceDefaults] =
    useState<WorkspaceDefaultsMap>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchModels = useCallback(async () => {
    setIsLoading(true);
    try {
      const [modelsData, providersData, defaultsData] = await Promise.all([
        workspaceSettingsJson<RegistryModelsResponse>(
          workspaceId,
          `/api/workspace/${encodeURIComponent(workspaceId)}/ai/registry/models`
        ),
        workspaceSettingsJson<RegistryProvidersResponse>(
          workspaceId,
          `/api/workspace/${encodeURIComponent(workspaceId)}/ai/registry/providers`
        ),
        workspaceSettingsJson<WorkspaceDefaultsResponse>(
          workspaceId,
          `/api/workspace/${encodeURIComponent(workspaceId)}/ai-defaults`
        ),
      ]);

      // Parse workspace defaults
      const d = defaultsData.defaults ?? {};
      setWorkspaceDefaults({
        chat: (d.chatModel as string | null | undefined) ?? null,
        image: (d.imageModel as string | null | undefined) ?? null,
        stt: (d.sttModel as string | null | undefined) ?? null,
        embedding: (d.embeddingModel as string | null | undefined) ?? null,
        rowMatching: (d.rowMatchingModel as string | null | undefined) ?? null,
        webSearch: (d.webSearchModel as string | null | undefined) ?? null,
      });

      const providerNames = new Map<string, string>();
      for (const p of providersData.providers ?? []) {
        providerNames.set(p.id, p.name.replace(/\s*\(.*?\)\s*$/, ''));
      }

      const allModels = modelsData.models ?? [];

      // Group by capability
      const capMap = new Map<string, AIModel[]>();

      for (const m of allModels) {
        if (m.status === 'deprecated' || !m.enabled) continue;

        const model: AIModel = {
          id: m.id,
          name: m.name,
          providerId: m.providerId,
          providerName: providerNames.get(m.providerId) ?? m.providerId,
          capabilities: m.capabilities,
          tier: m.tier,
          status: m.status,
          displayMultiplier: m.displayMultiplier ?? 1.0,
          isDefaultForCapability: m.isDefaultForCapability ?? null,
          enabled: m.enabled,
          workspaceDisabled: m.workspaceDisabled ?? false,
          configProviderId: m.configProviderId,
          configModelId: m.configModelId,
        };

        for (const cap of m.capabilities) {
          if (!capMap.has(cap)) {
            capMap.set(cap, []);
          }
          capMap.get(cap)!.push(model);
        }
      }

      // Build groups in defined order, then any remaining
      const groups: CapabilityGroup[] = [];
      const seen = new Set<string>();

      for (const cap of CAPABILITY_ORDER) {
        const models = capMap.get(cap);
        if (models && models.length > 0) {
          groups.push({
            capability: cap,
            models: sortModelsForDisplay(models),
          });
          seen.add(cap);
        }
      }

      // Any capabilities not in CAPABILITY_ORDER
      for (const [cap, models] of capMap) {
        if (!seen.has(cap) && models.length > 0) {
          groups.push({
            capability: cap,
            models: sortModelsForDisplay(models),
          });
        }
      }

      setCapabilityGroups(groups);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchModels().catch(console.error);

    const handler = () => {
      fetchModels().catch(console.error);
    };
    window.addEventListener('sl:ai-model-changed', handler);
    return () => window.removeEventListener('sl:ai-model-changed', handler);
  }, [fetchModels]);

  return { capabilityGroups, workspaceDefaults, isLoading };
}

// =============================================================================
// Components
// =============================================================================

/**
 * Row layout matching the Copilot chat sidebar preference popup row:
 *   Star(20) | Name(1fr) | Provider(auto) | Multiplier(48) | TierPill(auto)
 */
const MODEL_ROW_GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '20px 1fr auto 48px auto',
  alignItems: 'center',
  gap: 10,
  padding: '6px 0',
  fontSize: 13,
};

/** Capabilities where the credit multiplier is shown to users */
const SHOW_MULTIPLIER_CAPABILITIES = new Set(['chat']);

type ModelStarIconVariant = 'platform' | 'workspace' | 'none' | 'disabled';

const MODEL_STAR_ICON_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: 14,
  height: 14,
  fontSize: 14,
  lineHeight: 1,
};

const MODEL_DISABLED_STAR_SLASH_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: 1,
  right: 1,
  top: '50%',
  height: 2,
  background: 'var(--streamlain-error-color, #ef4444)',
  transform: 'rotate(-35deg)',
  transformOrigin: 'center',
};

function ModelStarIcon({
  variant,
  style,
}: {
  variant: ModelStarIconVariant;
  style?: React.CSSProperties;
}) {
  const isDisabled = variant === 'disabled';
  const glyph = variant === 'workspace' ? '\u2605' : '\u2606';

  return (
    <span
      aria-hidden="true"
      style={{
        ...MODEL_STAR_ICON_STYLE,
        color: isDisabled
          ? 'var(--streamlain-error-color, #ef4444)'
          : variant === 'none'
            ? 'var(--streamlain-icon-secondary)'
            : 'var(--streamlain-primary-color)',
        opacity: variant === 'none' ? 0.4 : 1,
        ...style,
      }}
    >
      {glyph}
      {isDisabled ? <span style={MODEL_DISABLED_STAR_SLASH_STYLE} /> : null}
    </span>
  );
}

function ModelRow({
  model,
  onSetDefault,
  onToggleDisabled,
  isAdmin,
  isWorkspaceDefault,
  isWorkspaceDisabled,
  capability,
}: {
  model: AIModel;
  onSetDefault?: (modelId: string, capability: string) => void;
  onToggleDisabled?: (model: AIModel, capability: string) => void;
  isAdmin: boolean;
  isWorkspaceDefault?: boolean;
  isWorkspaceDisabled?: boolean;
  capability: string;
}) {
  const isPlatformDefault = model.isDefaultForCapability === capability;

  const starState: 'workspace' | 'platform' | 'none' = isWorkspaceDefault
    ? 'workspace'
    : isPlatformDefault
      ? 'platform'
      : 'none';

  const starTitle = isWorkspaceDisabled
    ? 'Workspace disabled (click to allow)'
    : starState === 'workspace'
      ? 'Workspace Default (click to remove)'
      : starState === 'platform'
        ? `Platform Default${isAdmin ? ' (click to set as Workspace Default)' : ''}`
        : isAdmin
          ? 'Set as Workspace Default'
          : 'Only admins can set defaults';

  const showMultiplier = SHOW_MULTIPLIER_CAPABILITIES.has(capability);

  return (
    <div style={MODEL_ROW_GRID}>
      <button
        onClick={() => {
          if (!isAdmin) return;
          if (isWorkspaceDisabled && onToggleDisabled) {
            onToggleDisabled(model, capability);
            return;
          }
          if (isWorkspaceDefault && onToggleDisabled) {
            onToggleDisabled(model, capability);
            return;
          }
          if (onSetDefault) {
            onSetDefault(model.id, capability);
          }
        }}
        disabled={!isAdmin}
        title={starTitle}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: isAdmin ? 'pointer' : 'default',
          fontSize: 14,
          lineHeight: 1,
        }}
      >
        <ModelStarIcon variant={isWorkspaceDisabled ? 'disabled' : starState} />
      </button>

      <span
        style={{
          fontWeight: 500,
          color: isWorkspaceDisabled
            ? 'var(--streamlain-text-secondary-color)'
            : undefined,
          textDecoration: isWorkspaceDisabled ? 'line-through' : undefined,
          textDecorationColor: 'var(--streamlain-error-color, #ef4444)',
        }}
      >
        {model.name}
      </span>

      <span
        style={{
          color: 'var(--streamlain-text-secondary-color)',
          fontSize: 12,
          whiteSpace: 'nowrap',
        }}
      >
        {model.providerName}
      </span>

      <span
        style={{
          color: 'var(--streamlain-text-secondary-color)',
          fontSize: 12,
          fontWeight: 500,
          textAlign: 'right',
          visibility: showMultiplier ? 'visible' : 'hidden',
        }}
      >
        {model.displayMultiplier.toFixed(2)}x
      </span>

      <span style={tierPillStyle(model.tier)}>{tierLabel(model.tier)}</span>
    </div>
  );
}

/**
 * Flatten a capability group: sort by tier (professional → standard → economy),
 * then by multiplier descending within each tier, matching the Copilot
 * chat sidebar order.
 */
function sortModelsForDisplay(models: AIModel[]): AIModel[] {
  return sortModelsByTierAndCost(models, model => model.displayMultiplier);
}

/** Map capability to backend field name for workspace defaults */
const CAPABILITY_TO_BACKEND: Record<string, string> = {
  chat: 'chatModel',
  image: 'imageModel',
  stt: 'sttModel',
  embedding: 'embeddingModel',
  rowMatching: 'rowMatchingModel',
  webSearch: 'webSearchModel',
};

export const TierModelPanel = ({ isAdmin }: { isAdmin: boolean }) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;
  const { capabilityGroups, workspaceDefaults, isLoading } =
    useCapabilityModels(workspaceId);

  const handleSetDefault = useCallback(
    async (modelId: string, capability: string) => {
      const backendField = CAPABILITY_TO_BACKEND[capability];
      if (!backendField) {
        notify.error({ title: `Unknown capability: ${capability}` });
        return;
      }

      // __clear__ = remove workspace override
      const value = modelId === '__clear__' ? null : modelId;

      try {
        const res = await workspaceSettingsRequest(
          workspaceId,
          `/api/workspace/${encodeURIComponent(workspaceId)}/ai-defaults`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ [backendField]: value }),
          },
          'foreground'
        );
        if (res.ok) {
          notify.success({
            title: value
              ? 'Workspace default set'
              : 'Workspace override removed',
          });
          window.dispatchEvent(new CustomEvent('sl:ai-model-changed'));
        } else {
          const data = await res.json().catch(() => ({}));
          notify.error({
            title: data.message || 'Failed to set default',
          });
        }
      } catch {
        notify.error({ title: 'Network error' });
      }
    },
    [workspaceId]
  );

  const handleToggleDisabled = useCallback(
    async (model: AIModel, capability: string) => {
      try {
        const configData = await workspaceSettingsJson<ProviderConfigResponse>(
          workspaceId,
          `/api/workspace/${encodeURIComponent(workspaceId)}/ai/registry/provider-config`
        );
        const providerConfig = (configData.providers ?? []).find(
          (entry: { provider?: { id?: string } }) =>
            entry.provider?.id === (model.configProviderId ?? model.providerId)
        );
        const disabledModelIds = new Set<string>(
          providerConfig?.disabledModelIds ?? []
        );
        const configModelId = model.configModelId ?? model.id;
        if (model.workspaceDisabled) {
          disabledModelIds.delete(configModelId);
        } else {
          disabledModelIds.add(configModelId);
        }

        const res = await workspaceSettingsRequest(
          workspaceId,
          `/api/workspace/${encodeURIComponent(workspaceId)}/ai/registry/providers/${model.configProviderId ?? model.providerId}/config`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              enabled: providerConfig?.enabled ?? true,
              allowedTiers: providerConfig?.allowedTiers ?? [],
              disabledModelIds: Array.from(disabledModelIds),
            }),
          },
          'foreground'
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          notify.error({ title: data.message || 'Failed to update model' });
          return;
        }

        notify.success({
          title: model.workspaceDisabled
            ? 'Model enabled for workspace'
            : 'Model disabled for users',
        });
        if (
          !model.workspaceDisabled &&
          model.id === workspaceDefaults[capability]
        ) {
          await handleSetDefault('__clear__', capability);
          return;
        }
        window.dispatchEvent(new CustomEvent('sl:ai-model-changed'));
      } catch {
        notify.error({ title: 'Network error' });
      }
    },
    [handleSetDefault, workspaceDefaults, workspaceId]
  );

  if (isLoading) {
    return null;
  }

  const chatGroup = capabilityGroups.find(group => group.capability === 'chat');
  const executableChatModels =
    chatGroup?.models.filter(model => !model.workspaceDisabled) ?? [];
  if (executableChatModels.length === 0) {
    return (
      <div
        style={{
          color: 'var(--streamlain-text-secondary-color)',
          fontSize: 13,
        }}
      >
        {t['com.streamlain.settings.workspace.ai.models.none']()}
      </div>
    );
  }

  if (executableChatModels.length === 1) {
    const model = executableChatModels[0];
    const workspaceDefaultId = workspaceDefaults.chat ?? null;
    return (
      <div>
        <ModelRow
          model={model}
          isAdmin={false}
          isWorkspaceDefault={model.id === workspaceDefaultId}
          isWorkspaceDisabled={model.workspaceDisabled}
          capability="chat"
        />
        <div
          style={{
            color: 'var(--streamlain-text-secondary-color)',
            fontSize: 12,
          }}
        >
          {workspaceDefaultId === model.id
            ? t[
                'com.streamlain.settings.workspace.ai.models.workspace-default'
              ]()
            : t[
                'com.streamlain.settings.workspace.ai.models.platform-default'
              ]()}
        </div>
      </div>
    );
  }

  // Multiple chat models: existing workspace selector/allow-list UI.
  const visibleGroups = capabilityGroups.filter(
    g =>
      SETTINGS_CAPABILITIES.has(g.capability) &&
      !HIDDEN_CAPABILITIES.has(g.capability) &&
      g.models.length > 1
  );

  if (visibleGroups.length === 0) return null;

  return (
    <>
      {/* Legend */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          fontSize: 12,
          color: 'var(--streamlain-text-secondary-color)',
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <span>
          <ModelStarIcon variant="platform" style={{ marginRight: 4 }} />
          Platform Default
        </span>
        <span>
          <ModelStarIcon variant="workspace" style={{ marginRight: 4 }} />
          Workspace Default
        </span>
        <span>
          <ModelStarIcon variant="disabled" style={{ marginRight: 4 }} />
          Disabled for users
        </span>
      </div>
      {visibleGroups.map((group, groupIdx) => {
        const workspaceDefaultId = workspaceDefaults[group.capability] ?? null;
        const sorted = sortModelsForDisplay(group.models);
        return (
          <div
            key={group.capability}
            style={{
              marginBottom: groupIdx === visibleGroups.length - 1 ? 0 : 16,
            }}
          >
            {sorted.map(model => (
              <ModelRow
                key={model.id}
                model={model}
                onSetDefault={(modelId, capability) =>
                  void handleSetDefault(modelId, capability)
                }
                onToggleDisabled={(model, capability) =>
                  void handleToggleDisabled(model, capability)
                }
                isAdmin={isAdmin}
                isWorkspaceDefault={model.id === workspaceDefaultId}
                isWorkspaceDisabled={model.workspaceDisabled}
                capability={group.capability}
              />
            ))}
          </div>
        );
      })}
    </>
  );
};
